import { useState } from 'react';
import { Box, Collapse, IconButton, Stack, SxProps, Theme } from '@mui/material';
import { Typography, InfoOutlinedIcon, ExpandMoreIcon } from '@occc-app/design-system';
import type { FooterBannerDetailItem, FooterBannerSeverity } from './footer-info-banner.context';
import { FOOTER_INFO_BANNER_HEIGHT } from '../constants';

type FooterInfoBannerProps = {
  message: string;
  severity?: FooterBannerSeverity;
  details?: FooterBannerDetailItem[];
};

const severitySx: Record<FooterBannerSeverity, SxProps<Theme>> = {
  info: {
    bgcolor: 'info.light',
    color: 'text.primary',
  },
  error: {
    bgcolor: 'error.light',
    color: 'text.error',
  },
};

export const FooterInfoBanner = ({
  message,
  severity = 'info',
  details,
}: FooterInfoBannerProps) => {
  const [expanded, setExpanded] = useState(false);
  const hasDetails = !!details?.length;

  return (
    <>
      <Stack
        p={3}
        position='fixed'
        bottom={0}
        width='100%'
        spacing={1}
        direction='row'
        height={FOOTER_INFO_BANNER_HEIGHT}
        alignItems='center'
        boxShadow='0 -2px 4px rgba(0, 0, 0, 0.1)'
        sx={severitySx[severity]}
      >
        <InfoOutlinedIcon color={severity} />
        <Typography>{message}</Typography>
        {hasDetails && (
          <IconButton
            aria-label={expanded ? 'Collapse details' : 'Expand details'}
            aria-expanded={expanded}
            onClick={() => setExpanded((prev) => !prev)}
            color={severity}
            sx={{
              ml: 'auto',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        )}
      </Stack>

      {details && hasDetails && (
        <Collapse
          in={expanded}
          unmountOnExit
          sx={{
            position: 'fixed',
            bottom: FOOTER_INFO_BANNER_HEIGHT,
            width: '100%',
            boxShadow: '0 -2px 4px rgba(0, 0, 0, 0.1)',
            ...severitySx[severity],
          }}
        >
          <Box component='ol' sx={{ m: 0, px: 3, py: 2, pl: 6 }}>
            {details.map((item, index) => (
              <Box component='li' key={`${index}-${item.title}`}>
                <Typography component='span' fontWeight={600}>
                  {item.title}
                </Typography>
                {' — '}
                <Typography component='span'>{item.description}</Typography>
              </Box>
            ))}
          </Box>
        </Collapse>
      )}
    </>
  );
};
